import { Box, Skeleton, SkeletonCircle, Stack, Text } from '@chakra-ui/react';
import {
  useRef,
  useEffect,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { v4 as uuid } from 'uuid';
import invariant from 'tiny-invariant';
import { ProjectHistoryTimelineEvent } from './ProjectHistoryTimelineEvent';
import { CurrentVersionHeader } from './timeline/CurrentVersionHeader';
import { TimelineHandler } from './timeline/TimelineHandler';
import { TimelineLine } from './timeline/TimelineLine';
import { TimelineEventWrapper } from './timeline/TimelineEventWrapper';
import { useTimelineDrag } from './hooks/useTimelineDrag';
import { useResizeObserver } from './hooks/useResizeObserver';
import { getEventIcon, getEventRangePosition } from './utils/timelineUtils';
import {
  CreateHistoryEventDocument,
  EventResource,
  EventType,
  HistoryEventGroupDeepFragment,
  HistoryEventInput,
  UpdateHistoryEventDocument,
} from 'src/gql/graphql';
import { useS3Client } from 'src/hooks/s3';
import { copyS3State } from 'src/hooks/event-history';

// Props for the Timeline component
interface ProjectHistoryTimelineProps {
  eventGroups: HistoryEventGroupDeepFragment[];
  initialSelectedRange: [number, number];
  onSelectionChange?: (range: [number, number]) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  projectId: string;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  reloadEventGroups: () => Promise<HistoryEventGroupDeepFragment[]>;
}

// Helper functions to check selection status
const isIndexSelected = (index: number, range: [number, number]): boolean => {
  return index >= range[0] && index < range[1];
};

const isIndexInPreview = (
  index: number,
  isDragging: boolean,
  previewRange: [number, number] | null
): boolean => {
  return Boolean(
    isDragging &&
      previewRange &&
      index >= previewRange[0] &&
      index < previewRange[1]
  );
};

// Event component to reduce complexity
interface TimelineEventProps {
  eventGroup: HistoryEventGroupDeepFragment;
  index: number;
  selectedRange: [number, number];
  previewRange: [number, number] | null;
  isDragging: boolean;
  onEventClick: (index: number) => void;
  onEventDragStart: (e: React.MouseEvent, index: number) => void;
  isLastEvent: boolean;
  isFirstEvent?: boolean;
  updateRef: (
    el: HTMLDivElement | null,
    index: number,
    isLastEvent: boolean
  ) => void;
  isHovered: boolean;
  t: (key: string, options?: { count: number }) => string;
  projectId: string;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  isNextEventCheckpoint: boolean;
  reloadEventGroups: () => Promise<HistoryEventGroupDeepFragment[]>;
}

const TimelineEvent: React.FC<TimelineEventProps> = ({
  eventGroup,
  index,
  selectedRange,
  previewRange,
  isDragging,
  onEventClick,
  onEventDragStart,
  isLastEvent,
  isFirstEvent = false,
  updateRef,
  isHovered,
  setIsLoading,
  projectId,
  isNextEventCheckpoint,
  reloadEventGroups,
  t,
}) => {
  const s3Client = useS3Client(projectId);
  const [, createHistoryEventMutation] = useMutation(
    CreateHistoryEventDocument
  );
  const [, updateHistoryEvent] = useMutation(UpdateHistoryEventDocument);
  // Check if this event is in the actual selected range
  const isSelected = isIndexSelected(index, selectedRange);

  // Check if this event is in the preview range
  const isInPreview = isIndexInPreview(index, isDragging, previewRange);

  // Calculate position in selected range
  const { isFirst: isFirstInSelection, isLast: isLastInSelection } =
    getEventRangePosition(index, selectedRange);

  // For preview state, check if it's first or last in the preview range
  const { isFirst: isFirstInPreview, isLast: isLastInPreview } = previewRange
    ? getEventRangePosition(index, previewRange)
    : { isFirst: false, isLast: false };

  // Get the icon for this event type
  const eventIcon = getEventIcon(eventGroup.eventType || undefined);

  // Handle mouse down for drag selection
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.button === 0) {
      // Left mouse button
      onEventDragStart(e, index);
    }
  };

  const makeCheckPoint = useCallback(
    async (checkPointName: string) => {
      const srcKey = eventGroup.firstEvent?.stateKey;
      invariant(srcKey, 'Unable to find the source state');
      invariant(checkPointName, 'Check point name is missing');
      const destinationKeyFragments = srcKey.split('/');
      const destinationKey = `${destinationKeyFragments
        .slice(0, destinationKeyFragments.length - 1)
        .join('/')}/${uuid()}.json`;
      const creationDate = new Date(eventGroup.firstEvent?.createdAt ?? '');
      // This dance is meant to ensure that LAG function in the DB behaves correctly
      // If the copy the exact creation date then it lumped in the same group as its template
      creationDate.setTime(creationDate.getTime() - 100); // Subtract 100ms, No user can create items within this time range
      const checkPointPayload: HistoryEventInput = {
        ...eventGroup.firstEvent,
        eventName: checkPointName,
        eventType: EventType.Created,
        resourceType: EventResource.Project, // switched this for consistency with checkpoints created from current version
        resourceName: checkPointName,
        isCheckpoint: true,
        createdAt: creationDate.toISOString(),
        stateKey: destinationKey,
        metadata: {
          checkPointName,
        },
      };
      setIsLoading(true);
      try {
        // remove the copied ID so that we do not get conflicts in the DB
        delete checkPointPayload.id;
        invariant(
          checkPointPayload.isCheckpoint,
          'the payload should have the isCheckpoint flag set'
        );
        // Clone the s3 state
        await copyS3State(srcKey, destinationKey, s3Client);
        // insert the data into the backend
        await createHistoryEventMutation({
          input: { historyEvent: checkPointPayload },
        });
        await reloadEventGroups();
      } catch (e) {
        console.error('failed to make a checkpoint: ', e);
      } finally {
        setIsLoading(false);
      }
    },
    [eventGroup, s3Client, reloadEventGroups]
  );

  const handleCheckpointRename = useCallback(
    async (checkpointName: string) => {
      const checkpointId = eventGroup?.firstEvent?.id;
      invariant(
        checkpointId,
        'The checkpoint that you are attempting to rename does not exist'
      );
      invariant(checkpointName, 'A name was not provided when renaming');
      // If we have the same target as what we already have then do nothing
      if (checkpointName === eventGroup?.firstEvent?.resourceName) return;
      // TODO: Add spinner/loading effect
      // Show toasts to signal whether the update was successful or not
      try {
        setIsLoading(true);
        await updateHistoryEvent({
          input: {
            id: checkpointId,
            patch: {
              resourceName: checkpointName,
              eventName: checkpointName,
              metadata: { checkpointName },
            },
          },
        });
        await reloadEventGroups();
      } catch (e) {
        console.error('Failed to rename the checkpoint: ', e);
      } finally {
        setIsLoading(false);
      }
    },
    [eventGroup, reloadEventGroups]
  );

  return (
    <Box key={index}>
      <div ref={(el) => updateRef(el, index, isLastEvent)}>
        <TimelineEventWrapper
          isSelected={isSelected}
          isInPreview={isInPreview}
          isDragging={isDragging}
          isFirstInSelection={isFirstInSelection}
          isLastInSelection={isLastInSelection}
          isFirstInPreview={isFirstInPreview}
          isLastInPreview={isLastInPreview}
          icon={eventIcon}
          isCheckPoint={Boolean(eventGroup.isCheckpoint)}
          isNextEventCheckpoint={isNextEventCheckpoint}
          handleCheckPointCreation={makeCheckPoint}
          isFirstEvent={isFirstEvent}
          isHovered={isHovered}
        >
          <ProjectHistoryTimelineEvent
            eventGroup={eventGroup}
            isSelected={isSelected}
            isPreview={isInPreview}
            handleCheckpointRename={handleCheckpointRename}
            onClick={() => {
              if (!isDragging) {
                onEventClick(index);
              }
            }}
            onMouseDown={handleMouseDown}
            t={t}
          />
        </TimelineEventWrapper>
      </div>
    </Box>
  );
};

// eslint-disable-next-line complexity
export const ProjectHistoryTimeline: React.FC<ProjectHistoryTimelineProps> = ({
  eventGroups,
  initialSelectedRange,
  onSelectionChange,
  isLoading,
  isDisabled,
  setIsLoading,
  projectId,
  reloadEventGroups,
}) => {
  const { t } = useTranslation('project-history');

  // Container ref to get positions
  const timelineRef = useRef<HTMLDivElement>(null);

  // Refs for all event elements to measure their positions
  const eventRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Ref for Current Version dot
  const currentVersionDotRef = useRef<HTMLDivElement | null>(null);

  // Ref for last event dot
  const lastEventDotRef = useRef<HTMLDivElement | null>(null);

  // Setup event refs array
  useEffect(() => {
    eventRefs.current = eventRefs.current.slice(0, eventGroups.length);
    while (eventRefs.current.length < eventGroups.length) {
      eventRefs.current.push(null);
    }
  }, [eventGroups]);

  // Use resize observer
  const { positionsUpdateKey } = useResizeObserver({
    timelineRef,
    eventRefs,
    currentVersionDotRef,
    lastEventDotRef,
  });

  // Use drag functionality
  const {
    selectedRange,
    previewRange,
    isDragging,
    handleHandlerMouseDown,
    handleEventDrag,
    getHandlerPosition,
    setSelectedRange,
  } = useTimelineDrag({
    initialSelectedRange,
    eventRefs,
    timelineRef,
    eventsLength: eventGroups.length,
    onSelectionChange,
    currentVersionDotRef,
  });

  // Determine which range to use for handlers
  const displayRange =
    isDragging && previewRange ? previewRange : selectedRange;

  const selectedRangeLength = selectedRange[1] - selectedRange[0];

  const shouldShowTopHandler =
    selectedRange[0] !== 0 || selectedRangeLength !== 1;

  const shouldShowBottomHandler =
    selectedRange[1] !== eventGroups.length || selectedRangeLength !== 1;

  // Calculate positions for the selected range line
  const calculateSelectedLinePositions = () => {
    if (!timelineRef.current || !eventRefs.current) {
      return {
        isRangeSelected: false,
        selectedTop: undefined,
        selectedBottom: undefined,
      };
    }

    // Only use the actual selected range, not the preview during dragging
    const rangeToUse = selectedRange;

    // Special case: if only one item is selected but it's the first event,
    // we still want to show the line from the Current Version dot to the first event
    const isFirstEventSelected = rangeToUse[0] === 0;
    const shouldShowLineForSingleEvent =
      isFirstEventSelected && rangeToUse[1] === 1;

    // Normal case: Only show line when range includes at least 2 items
    // Special case: Also show line when first event is selected (even if range has only 1 item)
    if (rangeToUse[1] - rangeToUse[0] < 2 && !shouldShowLineForSingleEvent) {
      return {
        isRangeSelected: false,
        selectedTop: undefined,
        selectedBottom: undefined,
      };
    }

    const firstSelectedIndex = rangeToUse[0];
    const lastSelectedIndex = rangeToUse[1] - 1;

    // Special case: if first event is selected, use currentVersionDotRef instead of first event ref
    const firstSelectedElement = isFirstEventSelected
      ? currentVersionDotRef.current
      : eventRefs.current[firstSelectedIndex];
    const lastSelectedElement = eventRefs.current[lastSelectedIndex];

    if (!firstSelectedElement || !lastSelectedElement) {
      return {
        isRangeSelected: false,
        selectedTop: undefined,
        selectedBottom: undefined,
      };
    }

    const containerRect = timelineRef.current.getBoundingClientRect();
    const firstRect = firstSelectedElement.getBoundingClientRect();
    const lastRect = lastSelectedElement.getBoundingClientRect();

    // Get top position of first dot
    // For Current Version header dot, the dot position is different
    const selectedTop = isFirstEventSelected
      ? firstRect.top - containerRect.top + 24 // Adjust for Current Version dot position
      : firstRect.top - containerRect.top + 12; // Regular event dot position

    // Get bottom position of last dot (add dot size to position to get the bottom)
    const selectedBottom = lastRect.top - containerRect.top + 32; // 12px (dot position) + 24px (dot size)

    return {
      isRangeSelected: true,
      selectedTop,
      selectedBottom,
    };
  };

  // Calculate the bottom position of the timeline line based on the last event
  const calculateTimelineBottom = () => {
    if (
      !timelineRef.current ||
      !lastEventDotRef.current ||
      eventGroups.length === 0
    ) {
      return '0';
    }

    const containerRect = timelineRef.current.getBoundingClientRect();
    const lastEventRect = lastEventDotRef.current.getBoundingClientRect();

    // Calculate the distance from the bottom of the last event dot to the bottom of the container
    const bottomOffset = containerRect.bottom - (lastEventRect.top + 32); // 32px accounts for dot height

    return `${bottomOffset}px`;
  };

  // Calculate and memoize the positions when selectedRange or positionsUpdateKey changes
  const selectedLinePositions = useMemo(
    () => calculateSelectedLinePositions(),
    [selectedRange, positionsUpdateKey]
  );

  // Memoize the timeline bottom position
  const timelineBottom = useMemo(
    () => calculateTimelineBottom(),
    [eventGroups.length, positionsUpdateKey]
  );

  // Check if the first event is selected or in preview
  const isFirstEventSelected = selectedRange[0] === 0;
  const isFirstEventInPreview =
    isDragging && previewRange ? previewRange[0] === 0 : false;

  // Special case: if previously selected but not in preview during dragging
  const wasSelectedButDraggingAway =
    isDragging && isFirstEventSelected && previewRange && previewRange[0] !== 0;

  // State for shared hover between header and first event
  const [isFirstGroupHovered, setIsFirstGroupHovered] = useState(false);

  // Handler for mouse events on the first group
  const handleFirstGroupHover = (isHovering: boolean) => {
    setIsFirstGroupHovered(isHovering);
  };

  // If loading, show skeleton timeline
  if (isLoading) {
    return (
      <Box position="relative" paddingTop={2}>
        {/* Timeline vertical line */}
        <Skeleton
          position="absolute"
          left="20px"
          top="26px"
          bottom="48px"
          width="2px"
          zIndex={0}
        />

        {/* Skeleton events */}
        <Stack spacing={8} pl={10}>
          {Array.from({ length: 7 }).map((_, index) => (
            <Box key={`skeleton-${index}`} position="relative">
              {/* Event dot */}
              <Box position="absolute" left="-30px" top="0px">
                <SkeletonCircle size="6" />
              </Box>

              {/* Event content */}
              <Box>
                <Skeleton height="12px" width="80px" mb={3} />
                <Skeleton height="8px" width="180px" mb={3} />
                <Skeleton height="8px" width="140px" />
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  }

  if (eventGroups.length <= 0)
    return (
      <Text align="center" paddingTop={2}>
        Nothing to Display....
      </Text>
    );

  const isFirstEventACheckpoint = Boolean(eventGroups[0]?.isCheckpoint);
  return (
    <Box
      position="relative"
      ref={timelineRef}
      paddingTop={2}
      opacity={isDisabled ? 0.5 : 1}
      pointerEvents={isDisabled ? 'none' : 'auto'}
    >
      {/* Group container for CurrentVersionHeader and first event */}
      <Box
        onMouseEnter={() => handleFirstGroupHover(true)}
        onMouseLeave={() => handleFirstGroupHover(false)}
      >
        {/* Current Version header */}
        {!isFirstEventACheckpoint ? (
          <Box ref={currentVersionDotRef}>
            <CurrentVersionHeader
              label={t('timeline.current-version')}
              isSelected={isFirstEventSelected && !wasSelectedButDraggingAway}
              isInPreview={isFirstEventInPreview}
              isFirstInSelection={isFirstEventSelected}
              isFirstInPreview={isFirstEventInPreview}
              isHovered={isFirstGroupHovered}
              isNextEventCheckpoint={Boolean(eventGroups[0]?.isCheckpoint)}
              projectId={projectId}
              reloadEventGroups={reloadEventGroups}
              onClick={() => {
                if (!isDragging) {
                  const newRange: [number, number] = [0, 1];
                  setSelectedRange(newRange);
                }
              }}
              onMouseDown={(e) => {
                if (e.currentTarget !== e.target) return;
                handleEventDrag(e, 0);
              }}
            />
          </Box>
        ) : null}

        {/* First event (only render if events exist) */}
        {eventGroups.length > 0 && (
          <TimelineEvent
            key={0}
            eventGroup={eventGroups[0]}
            index={0}
            selectedRange={selectedRange}
            previewRange={previewRange}
            isDragging={isDragging}
            isFirstEvent={true}
            isHovered={isFirstGroupHovered}
            onEventClick={(i) => {
              if (!isDragging) {
                const newRange: [number, number] = [i, i + 1];
                setSelectedRange(newRange);
              }
            }}
            onEventDragStart={handleEventDrag}
            isLastEvent={eventGroups.length === 1}
            isNextEventCheckpoint={Boolean(eventGroups[1]?.isCheckpoint)}
            updateRef={(el, i, isLastEvent) => {
              eventRefs.current[i] = el;
              if (isLastEvent) {
                lastEventDotRef.current = el;
              }
            }}
            t={t}
            setIsLoading={setIsLoading}
            projectId={projectId}
            reloadEventGroups={reloadEventGroups}
          />
        )}
      </Box>

      {/* Rest of events (skip the first one) */}
      {eventGroups.slice(1).map((eventGroup, index) => {
        // Real index is index + 1 since we're skipping the first event
        const realIndex = index + 1;
        return (
          <TimelineEvent
            key={realIndex}
            eventGroup={eventGroup}
            index={realIndex}
            selectedRange={selectedRange}
            previewRange={previewRange}
            isDragging={isDragging}
            isFirstEvent={false}
            isHovered={false}
            onEventClick={(i) => {
              if (!isDragging) {
                const newRange: [number, number] = [i, i + 1];
                setSelectedRange(newRange);
              }
            }}
            onEventDragStart={handleEventDrag}
            isLastEvent={realIndex === eventGroups.length - 1}
            updateRef={(el, i, isLastEvent) => {
              eventRefs.current[i] = el;
              if (isLastEvent) {
                lastEventDotRef.current = el;
              }
            }}
            t={t}
            setIsLoading={setIsLoading}
            projectId={projectId}
            reloadEventGroups={reloadEventGroups}
            isNextEventCheckpoint={Boolean(
              eventGroups[realIndex + 1]?.isCheckpoint
            )}
          />
        );
      })}

      {/* Timeline vertical line */}
      <TimelineLine
        top="26px"
        bottom={timelineBottom}
        isRangeSelected={selectedLinePositions.isRangeSelected}
        selectedTop={selectedLinePositions.selectedTop}
        selectedBottom={selectedLinePositions.selectedBottom}
        selectedRange={selectedRange}
      />

      {/* Top Handler */}
      {displayRange[0] >= 0 && shouldShowTopHandler && (
        <TimelineHandler
          key={`top-handler-${positionsUpdateKey}`}
          position={getHandlerPosition(displayRange[0])}
          onMouseDown={(e) => handleHandlerMouseDown(e, true)}
          isDragging={isDragging}
        />
      )}

      {/* Bottom Handler */}
      {displayRange[1] <= eventGroups.length && shouldShowBottomHandler && (
        <TimelineHandler
          key={`bottom-handler-${positionsUpdateKey}`}
          position={getHandlerPosition(displayRange[1])}
          onMouseDown={(e) => handleHandlerMouseDown(e, false)}
          isDragging={isDragging}
        />
      )}
    </Box>
  );
};
